import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { Konto, LobbyZeile, Me, Protokollzeile, Systemstand } from '../lib/types';

/**
 * Die Verwaltung des Super-Admins.
 *
 * Drei Abschnitte: was im System ist, wer ein Konto hat, und was geschehen ist. Alles davon ist
 * lesend oder beendend – mitspielen kann der Super-Admin nicht, auch nicht von hier aus.
 *
 * Die Zahlen oben sind bewusst nüchtern. Ein Verwaltungsbildschirm, der Zustände mit Ampelfarben
 * bewertet, verleitet dazu, Grün als „alles in Ordnung“ zu lesen – obwohl niemand geprüft hat,
 * was „in Ordnung“ überhaupt heißen soll.
 */
export function AdminView({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const [abschnitt, setAbschnitt] = useState<'SYSTEM' | 'KONTEN' | 'PROTOKOLL' | 'TWITCH'>('SYSTEM');

  if (me.role !== 'SUPER_ADMIN') {
    return (
      <main className="page narrow">
        <h1>Verwaltung</h1>
        <p className="notice">Diese Ansicht ist dem Super-Admin vorbehalten.</p>
        <button type="button" onClick={onLogout}>
          Abmelden
        </button>
      </main>
    );
  }

  return (
    <main className="page">
      <header className="page-head">
        <div>
          <p className="muted small">Super-Admin</p>
          <h1>Verwaltung</h1>
        </div>
        <div className="row">
          <button type="button" onClick={() => window.location.assign('/')}>
            Zu den Lobbys
          </button>
          <button type="button" className="ghost" onClick={onLogout}>
            Abmelden
          </button>
        </div>
      </header>

      <nav className="tabs">
        {(
          [
            ['SYSTEM', 'System'],
            ['KONTEN', 'Konten'],
            ['PROTOKOLL', 'Protokoll'],
            ['TWITCH', 'Twitch / SirBotrick'],
          ] as const
        ).map(([id, titel]) => (
          <button
            key={id}
            type="button"
            className={abschnitt === id ? 'tab active' : 'tab'}
            onClick={() => setAbschnitt(id)}
          >
            {titel}
          </button>
        ))}
      </nav>

      {abschnitt === 'SYSTEM' && <System />}
      {abschnitt === 'KONTEN' && <Konten meineId={me.userId} />}
      {abschnitt === 'PROTOKOLL' && <Protokoll />}
      {abschnitt === 'TWITCH' && <BotVerwaltung />}
    </main>
  );
}

// --- System ------------------------------------------------------------------

function System() {
  const [stand, setStand] = useState<Systemstand | null>(null);
  const [lobbys, setLobbys] = useState<LobbyZeile[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    void api
      .systemstand()
      .then(setStand)
      .catch((err) => setFehler(err instanceof ApiError ? err.message : 'Nicht erreichbar.'));
    void api.lobbies().then(setLobbys).catch(() => setLobbys([]));
  }, []);

  if (fehler) return <p className="notice">{fehler}</p>;
  if (!stand) return <p className="muted">einen Moment …</p>;

  const zahlen: [string, string | number][] = [
    ['Lobbys', `${stand.lobbys.offen} offen von ${stand.lobbys.gesamt}`],
    ['davon Testlobbys', stand.lobbys.test],
    ['Konten', stand.konten.gesamt],
    ['Offene Sitzungen', stand.sitzungen.offen],
    ['Teams', stand.inhalte.teams],
    ['Aufgaben im Pool', stand.inhalte.aufgaben],
    ['Belegte Felder', stand.inhalte.belegungen],
    ['Abstimmungen gelaufen', stand.inhalte.abstimmungen],
    ['Twitch-Kanäle', stand.twitch.kanäle],
    ['Datenbank', stand.datenbank.version ?? 'erreichbar'],
  ];

  return (
    <div className="stack">
      <section className="panel">
        <h2>Im System</h2>
        <dl className="kennzahlen">
          {zahlen.map(([name, wert]) => (
            <div key={name}>
              <dt>{name}</dt>
              <dd>{wert}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="panel">
        <h2>Konten nach Rolle</h2>
        <ul className="zeilen">
          {Object.entries(stand.konten.nachRolle).map(([rolle, anzahl]) => (
            <li key={rolle} className="zeile">
              <span className="zeile-text">{rolle}</span>
              <span>{anzahl}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="panel">
        <h2>Alle Lobbys</h2>
        <ul className="zeilen">
          {lobbys.map((lobby) => (
            <li key={lobby.id} className="zeile">
              <button
                type="button"
                className="zeile-text link"
                onClick={() => window.location.assign(`/lobby/${lobby.id}`)}
              >
                {lobby.name}
              </button>
              <span className="muted small">
                {lobby.boardSize}×{lobby.boardSize} · {lobby.state}
                {lobby.isTest ? ' · Test' : ''}
              </span>
            </li>
          ))}
          {lobbys.length === 0 && <li className="muted small">Noch keine Lobby.</li>}
        </ul>
      </section>
    </div>
  );
}

// --- Konten ------------------------------------------------------------------

function Konten({ meineId }: { meineId: string }) {
  const [konten, setKonten] = useState<Konto[] | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [läuft, setLäuft] = useState(false);

  const laden = () => {
    void api
      .konten()
      .then(setKonten)
      .catch(() => setKonten([]));
  };
  useEffect(laden, []);

  async function tun(was: () => Promise<unknown>, erfolg: string) {
    setLäuft(true);
    setMeldung(null);
    try {
      await was();
      setMeldung(erfolg);
      laden();
    } catch (err) {
      setMeldung(err instanceof ApiError ? err.message : 'Das hat nicht geklappt.');
    } finally {
      setLäuft(false);
    }
  }

  return (
    <section className="panel">
      <h2>Konten</h2>
      <ul className="zeilen">
        {(konten ?? []).map((konto) => {
          const ichSelbst = konto.id === meineId;
          return (
            <li key={konto.id} className={konto.gesperrt ? 'zeile inaktiv' : 'zeile'}>
              <span className="zeile-text">
                {konto.username}
                {ichSelbst && <span className="muted small"> · das bist du</span>}
              </span>
              <span className="muted small">{konto.role}</span>
              <span className="muted small">
                {konto.angemeldet ? 'angemeldet' : 'nicht angemeldet'}
                {konto.mfaBestätigt ? ' · 2FA' : ''}
              </span>

              {/* Das eigene Konto bleibt unantastbar – wer sich beim Aufräumen selbst aussperrt,
                  macht die Lage schlimmer. */}
              {!ichSelbst && (
                <>
                  <button
                    type="button"
                    disabled={läuft || !konto.angemeldet}
                    onClick={() =>
                      void tun(() => api.beendeSitzungen(konto.id), 'Sitzungen beendet.')
                    }
                  >
                    Abmelden
                  </button>
                  <button
                    type="button"
                    className={konto.gesperrt ? 'ghost' : 'ghost gefahr'}
                    disabled={läuft}
                    onClick={() =>
                      void tun(
                        () => api.setzeGesperrt(konto.id, !konto.gesperrt),
                        konto.gesperrt ? 'Entsperrt.' : 'Gesperrt.',
                      )
                    }
                  >
                    {konto.gesperrt ? 'Entsperren' : 'Sperren'}
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
      <p className="muted small">
        Sperren beendet auch die laufenden Sitzungen – sonst bliebe die Sperre folgenlos, solange
        jemand angemeldet ist.
      </p>
      {meldung && <p className="muted small">{meldung}</p>}
    </section>
  );
}

// --- Protokoll ---------------------------------------------------------------

function Protokoll() {
  const [zeilen, setZeilen] = useState<Protokollzeile[]>([]);
  const [arten, setArten] = useState<string[]>([]);
  const [art, setArt] = useState('');
  const [fertig, setFertig] = useState(false);
  const [läuft, setLäuft] = useState(false);

  const laden = (vor?: string) => {
    setLäuft(true);
    void api
      .protokoll({ limit: 50, action: art || undefined, vor })
      .then((neu) => {
        setZeilen((bisher) => (vor ? [...bisher, ...neu] : neu));
        // Weniger als angefragt heißt: Es kommt nichts mehr.
        setFertig(neu.length < 50);
      })
      .finally(() => setLäuft(false));
  };

  useEffect(() => {
    setFertig(false);
    laden();
  }, [art]);

  useEffect(() => {
    void api.protokollArten().then(setArten).catch(() => setArten([]));
  }, []);

  return (
    <section className="panel">
      <h2>Aktionsprotokoll</h2>

      <label>
        Nur diese Handlung
        <select value={art} onChange={(e) => setArt(e.target.value)}>
          <option value="">alle</option>
          {arten.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </label>

      <ul className="protokoll">
        {zeilen.map((zeile) => (
          <li key={zeile.id}>
            <time dateTime={zeile.at}>{new Date(zeile.at).toLocaleString('de-DE')}</time>
            <code>{zeile.action}</code>
            <span>{zeile.actorName ?? zeile.actorLabel}</span>
            <span className="muted small">{zeile.lobbyName ?? ''}</span>
            {zeile.detail != null && (
              <span className="protokoll-detail">{JSON.stringify(zeile.detail)}</span>
            )}
          </li>
        ))}
        {zeilen.length === 0 && !läuft && <li className="muted small">Nichts eingetragen.</li>}
      </ul>

      {/* Geblättert wird über den Zeitpunkt, nicht über eine Seitenzahl: Während man liest,
          kommen neue Zeilen hinzu, und Seitenzahlen verrutschen dann. */}
      {!fertig && zeilen.length > 0 && (
        <button
          type="button"
          disabled={läuft}
          onClick={() => laden(zeilen[zeilen.length - 1]!.at)}
        >
          Ältere laden
        </button>
      )}
    </section>
  );
}

function BotVerwaltung() {
  const [stand, setStand] = useState<{ connected: boolean; login: string | null; expiresAt: string | null } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const laden = () => void api.botStatus().then(setStand).catch(e => setFehler(e instanceof Error ? e.message : 'Status nicht erreichbar.'));
  useEffect(() => { laden(); const status = new URLSearchParams(window.location.search).get('bot');
    if (status) { setFehler(status === 'ok' ? null : `Twitch-Anmeldung: ${status}`); window.history.replaceState(null, '', '/admin'); }
  }, []);
  async function verbinden() {
    setBusy(true); setFehler(null);
    try { const { url } = await api.botAuthorize(); window.location.assign(url); }
    catch (e) { setFehler(e instanceof Error ? e.message : 'Verbindung fehlgeschlagen.'); setBusy(false); }
  }
  async function trennen() {
    if (!window.confirm('SirBotrick wirklich von Twitch trennen?')) return;
    setBusy(true); setFehler(null);
    try { await api.botDisconnect(); laden(); }
    catch (e) { setFehler(e instanceof Error ? e.message : 'Trennen fehlgeschlagen.'); }
    finally { setBusy(false); }
  }
  return <section className="panel stack">
    <h2>SirBotrick – Twitch-Bot</h2>
    <p className="muted small">Das zentrale Botkonto für alle verbundenen Bingo-Twitch-Kanäle.</p>
    <p><strong>Status:</strong> {stand === null ? 'Wird geladen …' : stand.connected ? `Verbunden als ${stand.login}` : 'Nicht verbunden'}</p>
    {stand?.expiresAt && <p className="muted small">Aktuelles Zugangstoken gültig bis: {new Date(stand.expiresAt).toLocaleString('de-DE')}. Es wird automatisch erneuert.</p>}
    <div className="row">
      <button type="button" disabled={busy} onClick={() => void verbinden()}>{stand?.connected ? 'SirBotrick erneut anmelden' : 'SirBotrick mit Twitch verbinden'}</button>
      {stand?.connected && <button type="button" className="ghost gefahr" disabled={busy} onClick={() => void trennen()}>Verbindung trennen</button>}
      <button type="button" className="ghost" onClick={laden}>Status aktualisieren</button>
    </div>
    <p className="muted small">Bei Twitch unbedingt mit dem Konto sirbotrick anmelden. Der Bot erhält ausschließlich Chat-Lese- und Schreibrechte.</p>
    {fehler && <p className="notice">{fehler}</p>}
  </section>;
}
